const errorStatuses = [
  "failed",
  "error",
  "invalid_plan",
  "invalid_zip",
  "invalid_iccid",
  "timeout",
  "suspended",
  "invalid_areacode",
  "cancelled",
  "canceled",
  "invalid_msisdn",
  "invalid_mdn",
  "invalid_status",
  "cooldown",
  "sim not found.",
  "offline",
  "invalid_organization",
  "credential_not_found",
  "imsi_not_found",
  "update_error",
  "address_error",
  "data_limit_error",
  "plan_error",
  "action_required",
  "webhook_error",
  "rate_limited",
  "outage",
  "no_active_subscription",
  "invalid_operation",
  "not_deactivated",
  "not_paused",
];

const pendingStatuses = [
  "pending",
  "in_progress",
  "submitted",
  "portin_request",
  "update_request",
  "webhook_pending",
];

const telitChangeNumberPendingStatuses = [
  ...pendingStatuses,
  "deactivating",
  "deactivated",
  "activating",
  "activated",
];

// Shown as info chips; counted as success.
const infoStatuses = [
  "already_active",
  "sim_inactive",
  "already_deactivated",
  "already_paused",
  "already_refilled",
  "already_active_bad_msisdn",
  "already_ported",
];

const successStatuses = [
  ...infoStatuses,
  "active",
  "activated",
  "activating",
  "available",
  "already_sent",
  "changed",
  "completed",
  "deactivated",
  "deactivating",
  "existing_line",
  "partial",
  "paused",
  "ported",
  "restored",
  "success",
  "unpaused",
  "adjusted",
  "deactivate_requested",
  "provisioned",
  "plan_applied",
];

const toStatusSet = (statuses) => new Set(statuses.map((status) => status.toLowerCase()));
const errorStatusSet = toStatusSet(errorStatuses);
const pendingStatusSet = toStatusSet(pendingStatuses);
const telitChangeNumberPendingStatusSet = toStatusSet(telitChangeNumberPendingStatuses);
const infoStatusSet = toStatusSet(infoStatuses);
const successStatusSet = toStatusSet(successStatuses);

const getPendingStatusSet = (telitChangeNumber) =>
  telitChangeNumber ? telitChangeNumberPendingStatusSet : pendingStatusSet;

const CHIP_COLOR_BY_CATEGORY = {
  pending: "warning",
  error: "error",
  info: "info",
  success: "success",
};

const classifyStatus = (status, telitChangeNumber = false) => {
  const normalized = status?.toString().trim().toLowerCase();
  // Sparq IMSI status reports "online" in various casings/formats; always success.
  if (/\bonline\b/.test(normalized || "")) {
    return "success";
  }
  if (getPendingStatusSet(telitChangeNumber).has(normalized)) {
    return "pending";
  }
  if (errorStatusSet.has(normalized)) {
    return "error";
  }
  if (infoStatusSet.has(normalized)) {
    return "info";
  }
  if (successStatusSet.has(normalized)) {
    return "success";
  }
  // Any status we don't recognize as a known pending/success/info state is
  // surfaced as an error rather than silently counted as a success.
  return "error";
};

const getChipColor = (status, telitChangeNumber = false) =>
  CHIP_COLOR_BY_CATEGORY[classifyStatus(status, telitChangeNumber)];

const getStatusCount = (statusData, telitChangeNumber = false) => {
  const count = { success: 0, error: 0, pending: 0 };

  Object.keys(statusData).forEach((status) => {
    const category = classifyStatus(status, telitChangeNumber);
    const bucket = category === "info" ? "success" : category;
    count[bucket] += statusData[status];
  });

  return count;
};

const CLIENTS = {
  ultra: "ultra",
  telit: "telit",
  sparq: "sparq",
  cricket: "cricket",
  verizon: "verizon",
  tmobile: "tmobile",
  selectel: "selectel",
  imsi: "imsi",
  opn: "opn",
  tmobilej: "tmobilej",
  tmobilej_workaround: "tmobilej_workaround",
  vs: "vs",
  it: "it",
  nyx: "nyx",
  pwg: "pwg",
};

const OPERATIONS = {
  activate: "activate",
  change_number: "change_number",
  change_plan: "change_plan",
  change_sim: "change_sim",
  deactivate: "deactivate",
  pause: "pause",
  restore: "restore",
  sim_info: "sim_info",
  unpause: "unpause",
  refill: "refill",
  portin: "portin",
  portout: "portout",
  reset: "reset",
  clear_status: "clear_status",
  hotline: "hotline",
  wifi_calling: "wifi_calling",
  imsi_create: "imsi_create",
  imsi_delete: "imsi_delete",
  imsi_workflow: "imsi_workflow",
  order_info: "order_info",
  assign_filter_groups: "assign_filter_groups",
  update_number: "update_number",
  mms_update: "mms_update",
};

const NOT_RETRY_STATUSES = ["active", "already_active", "completed", "changed"];
class CustomError extends Error {
  constructor(message) {
    super(message);
    this.name = "CustomError";
  }
}

const getValidRetryLines = (batchData) =>
  batchData.reduce((acc, item) => {
    const extraData = typeof item.extra_data === 'string' 
      ? JSON.parse(item.extra_data || '{}') 
      : (item.extra_data || {});
    
    if (
      !NOT_RETRY_STATUSES.includes(item.status) &&
      (extraData.user_zip || extraData.user_areacode)
    ) {
      acc.push(item);
    }
    return acc;
  }, []);

export {
  getChipColor,
  CLIENTS,
  getStatusCount,
  OPERATIONS,
  NOT_RETRY_STATUSES,
  CustomError,
  getValidRetryLines,
};
